import { client } from "@/hono";
import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { Fragment } from "react";
import { useNavigate } from "react-router";
import { useCalculation } from "@/hooks/use-calculation";
import { useCitadel } from "@/hooks/use-citadel";
import { calculationTabPath } from "./tabs";

/** Props Interface */
interface CalculationMenuProps {
	calculationId: string;
}

export function CalculationMenu({ calculationId }: CalculationMenuProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const convertLoader = useLoading();
	const lockLoader = useLoading();
	const unlockModal = useAsyncModal();
	const qc = useQueryClient();
	const { citadel } = useCitadel();

	/** Query */
	const { calculation } = useCalculation(calculationId);

	/** Functions */
	function handleOnNavigateToEdit() {
		const returnTo = calculationTabPath(calculationId, "services");
		navigate(`/calculations/${calculationId}/editor?returnTo=${encodeURIComponent(returnTo)}`);
	}

	async function handleOnConvertToProject() {
		try {
			convertLoader.start();
			const response = await client.calculations.offers[":calculationId"].convert.$post({
				param: { calculationId },
			});
			const data = await responseHandler(response);
			if (!data.project?.id) throw new Error("");
			navigate(`/projects/${data.project?.id}`);
		} catch (error) {
			toast.error("Error");
			console.error(error);
		} finally {
			convertLoader.stop();
		}
	}

	async function handleOnToggleLock() {
		const isLocked = calculation?.state !== "locked";
		if (!isLocked && !(await unlockModal.confirm())) return;
		try {
			lockLoader.start();
			const response = await client.calculations[":calculationId"].lock.$patch({
				param: { calculationId },
				json: { isLocked },
			});
			await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate(["calculation", calculationId]) });
			toast.success(
				t(isLocked ? "calculations.lock_successful" : "calculations.unlock_successful"),
			);
		} finally {
			lockLoader.stop();
		}
	}

	/** Computed */
	// Locking and unlocking ask `approve Calculation`, which the member template does not carry.
	const lockIsDenied =
		!!citadel &&
		citadel.cannot("approve", "Calculation", calculation ? { ...calculation } : undefined);
	const isLocked = calculation?.state === "locked";
	// Both pairs the route gates on, the same test the calculations list already makes.
	const convertIsDenied =
		!!citadel &&
		(citadel.cannot("update", "Calculation", calculation ? { ...calculation } : undefined) ||
			citadel.cannot("create", "Project"));

	/** Render */
	return (
		<Fragment>
			<div className="h-stack">
				{!lockIsDenied && calculation && (
					<Button
						icon={<Icon name={isLocked ? "LockOpen" : "Lock"} />}
						isLoading={lockLoader.isLoading}
						onClick={handleOnToggleLock}
					>
						{t(isLocked ? "calculations.unlock" : "calculations.lock")}
					</Button>
				)}
				{!convertIsDenied && (
					<Button
						variant="primary"
						icon={<Icon name="ChevronsRight" />}
						isLoading={convertLoader.isLoading}
						onClick={handleOnConvertToProject}
					>
						{t("calculations.convert_to_project")}
					</Button>
				)}
				<Button
					variant="primary"
					icon={<Icon name="Edit" />}
					onClick={handleOnNavigateToEdit}
				>
					{t("general.edit")}
				</Button>
			</div>
			{unlockModal.isOpen && (
				<ConfirmModal
					controller={unlockModal}
					title={t("calculations.unlock_confirm_title")}
					message={t("calculations.unlock_confirm_message")}
				/>
			)}
		</Fragment>
	);
}
